import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Button,
    Paper,
    alpha
} from '@mui/material';
import { Close } from '@mui/icons-material';

export default function ReleaseHighlightBanner({ latestRelease, onViewFull }) {
    const { version, date, client_updates } = latestRelease;
    const storageKey = 'dismissedReleaseVersion';

    // only show if not dismissed
    const [visible, setVisible] = useState(() => {
        return localStorage.getItem(storageKey) !== version;
    });

    // build up to 2 summary lines
    const allItems = [
        ...(client_updates.features || []),
        ...(client_updates.improvements || []),
        ...(client_updates.bug_fixes || [])
    ];
    const highlights = allItems.slice(0, 2);

    const dismiss = () => {
        localStorage.setItem(storageKey, version);
        setVisible(false);
    };

    // reset banner if version changes
    useEffect(() => {
        if (localStorage.getItem(storageKey) !== version) {
            setVisible(true);
        }
    }, [version]);

    if (!visible || highlights.length === 0) {
        return null;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', p: 4 }}>
            <Paper
                elevation={0}
                sx={{
                    position: 'relative',
                    borderLeft: theme => `4px solid ${theme.palette.primary.main}`,
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
                    p: 3,
                    alignItems: 'flex-start',
                    borderRadius: 2,
                }}
            >
                {/* Version pill + date */}
                <Box sx={{ mr: 2, mb: 2 }}>
                    <Typography
                        component="span"
                        sx={{
                            bgcolor: theme => theme.palette.primary.main,
                            color: '#fff',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontWeight: 700,
                        }}
                    >
                        {version}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ ml: 1, color: 'text.secondary', display: 'inline-block', fontWeight: 500 }}
                    >
                        {new Date(date).toLocaleDateString()}
                    </Typography>
                </Box>

                {/* Title + bullets */}
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                        {latestRelease.title}
                    </Typography>
                    {highlights.map((txt, i) => (
                        <Typography 
                            key={i} 
                            variant="body1" 
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                mb: i < highlights.length - 1 ? 1.5 : 0,
                                fontWeight: 500
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: 8,
                                    height: 8,
                                    bgcolor: theme => theme.palette.success.main,
                                    borderRadius: '50%',
                                    display: 'inline-block',
                                    mr: 1.5
                                }}
                            />
                            {txt}
                        </Typography>
                    ))}

                    <Button
                        variant="text"
                        color="primary"
                        onClick={() => {
                            if (typeof onViewFull === 'function') onViewFull();
                            const elt = document.getElementById('full-release-log');
                            if (elt) elt.scrollIntoView({ behavior: 'smooth' });
                        }}
                        disableRipple
                        disableElevation
                        sx={{
                            mt: 2,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.95rem',
                            px: 1.5,
                            py: 0.75,
                            alignSelf: 'flex-start',
                            borderRadius: 1.5,
                            color: 'primary.main',
                            '&:hover': {
                            bgcolor: 'action.hover', // ✅ Soft button-like hover
                            },
                            '&:focus': {
                            outline: 'none',
                            },
                        }}
                        >
                        View Full Release Log
                        </Button>


                </Box>

                {/* Dismiss */}
                <IconButton
                    size="small"
                    onClick={dismiss}
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                >
                    <Close />
                </IconButton>
            </Paper>
        </Box>
    );
}
